<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useRouter } from 'vue-router';

import MessageApi from 'dashboard/api/inbox/message';
import { OnClickOutside } from '@vueuse/components';

const props = defineProps({
  conversation: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['close', 'reassigned']);

const store = useStore();
const router = useRouter();
const { accountId } = useAccount();

const agents = useMapGetter('agents/getVerifiedAgents');

const messages = ref([]);
const isLoadingMessages = ref(false);
const selectedAgentId = ref(null);
const isReassigning = ref(false);
const messagesContainer = ref(null);
const showAgentDropdown = ref(false);

const selectedAgentName = computed(() => {
  if (!selectedAgentId.value) return null;
  const agent = (agents.value || []).find(a => a.id === selectedAgentId.value);
  return agent?.name || null;
});

const pickAgent = agent => {
  selectedAgentId.value = agent.id;
  showAgentDropdown.value = false;
};

const contact = computed(() => props.conversation.meta?.sender || {});
const currentAssignee = computed(() => props.conversation.meta?.assignee || {});
const conversationId = computed(() => props.conversation.id);

// Watch for real-time message updates from store
const storeConversation = computed(() =>
  store.getters.getConversationById(conversationId.value)
);

const storeMessages = computed(() => {
  const conv = storeConversation.value;
  if (!conv || !conv.messages) return [];
  return [...conv.messages].sort((a, b) => a.created_at - b.created_at);
});

// Sync messages from store when new ones arrive
watch(
  storeMessages,
  newMessages => {
    if (newMessages.length > messages.value.length) {
      messages.value = newMessages;
      nextTick(() => scrollToBottom());
    }
  },
  { deep: true }
);

const scrollToBottom = () => {
  if (messagesContainer.value) {
    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
  }
};

const fetchMessages = async () => {
  isLoadingMessages.value = true;
  try {
    // Try loading from store first
    const conv = storeConversation.value;
    if (conv && conv.messages && conv.messages.length > 0) {
      messages.value = [...conv.messages].sort(
        (a, b) => a.created_at - b.created_at
      );
    } else {
      // Fetch from API
      const { data } = await MessageApi.getPreviousMessages({
        conversationId: conversationId.value,
      });
      messages.value = (data.payload || []).sort(
        (a, b) => a.created_at - b.created_at
      );
    }
    nextTick(() => scrollToBottom());
  } catch (err) {
    // silent
  } finally {
    isLoadingMessages.value = false;
  }
};

const formatTime = timestamp => {
  if (!timestamp) return '';
  const date = new Date(timestamp * 1000);
  return date.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDate = timestamp => {
  if (!timestamp) return '';
  const date = new Date(timestamp * 1000);
  const today = new Date();
  if (date.toDateString() === today.toDateString()) return 'Hoje';
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return 'Ontem';
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
  });
};

const messageGroups = computed(() => {
  const groups = [];
  let currentDate = '';
  messages.value.forEach(msg => {
    const date = formatDate(msg.created_at);
    if (date !== currentDate) {
      currentDate = date;
      groups.push({ type: 'date', label: date });
    }
    groups.push({ type: 'message', data: msg });
  });
  return groups;
});

const isOutgoing = msg => msg.message_type === 1;
const isActivity = msg => msg.message_type === 2;

const reassignAgent = async () => {
  if (!selectedAgentId.value) return;
  isReassigning.value = true;
  try {
    await store.dispatch('assignAgent', {
      conversationId: conversationId.value,
      agentId: selectedAgentId.value,
    });
    emit('reassigned');
  } catch (err) {
    // silent
  } finally {
    isReassigning.value = false;
    selectedAgentId.value = null;
  }
};

const resolveConversation = async () => {
  await store.dispatch('toggleStatus', {
    conversationId: conversationId.value,
    status: 'resolved',
  });
  emit('close');
};

const openFullConversation = () => {
  router.push({
    name: 'inbox_conversation',
    params: {
      accountId: accountId.value,
      conversation_id: conversationId.value,
    },
  });
};

onMounted(() => {
  fetchMessages();
  // Ensure the conversation is loaded in store for real-time updates
  store.dispatch('getConversation', conversationId.value);
});

const previewImgError = ref(false);

// Refetch when conversation changes
watch(conversationId, () => {
  previewImgError.value = false;
  fetchMessages();
  store.dispatch('getConversation', conversationId.value);
});
</script>

<template>
  <Teleport to="body">
    <div
      class="fixed inset-0 bg-black/20 dark:bg-black/40 z-50 transition-opacity"
      @click="emit('close')"
    />
    <div
      class="fixed top-0 right-0 h-full w-[460px] max-w-[90vw] bg-n-background border-l border-n-weak z-50 flex flex-col shadow-2xl"
    >
      <div
        class="flex items-center justify-between px-4 py-3 border-b border-n-weak bg-n-surface-1"
      >
        <div class="flex items-center gap-3 min-w-0">
          <img
            v-if="contact.thumbnail && !previewImgError"
            :src="contact.thumbnail"
            :alt="contact.name"
            class="size-9 rounded-full object-cover ring-1 ring-n-weak"
            @error="previewImgError = true"
          />
          <div
            v-else
            class="size-9 rounded-full bg-n-brand/10 text-n-brand flex items-center justify-center text-sm font-semibold"
          >
            {{ (contact.name || '?')[0].toUpperCase() }}
          </div>
          <div class="min-w-0">
            <p class="text-sm font-semibold text-n-slate-12 truncate">
              {{ contact.name || 'Contato' }}
            </p>
            <p class="text-xs text-n-slate-9">
              #{{ conversationId }}
              <span v-if="contact.phone_number"
class="ml-1"
                >· {{ contact.phone_number }}</span>
            </p>
          </div>
        </div>
        <div class="flex items-center gap-1">
          <button
            class="p-1.5 rounded-lg hover:bg-n-alpha-2 text-n-slate-10 hover:text-n-slate-12 transition-colors"
            title="Abrir conversa completa"
            @click="openFullConversation"
          >
            <span class="i-lucide-external-link size-4" />
          </button>
          <button
            class="p-1.5 rounded-lg hover:bg-n-alpha-2 text-n-slate-10 hover:text-n-slate-12 transition-colors"
            @click="emit('close')"
          >
            <span class="i-lucide-x size-4" />
          </button>
        </div>
      </div>

      <div
        v-if="currentAssignee.id"
        class="flex items-center gap-2 px-4 py-2 border-b border-n-weak bg-n-alpha-1 text-xs text-n-slate-10"
      >
        <span class="i-lucide-user size-3" />
        Atribuída a:
        <strong class="text-n-slate-12">{{ currentAssignee.name }}</strong>
      </div>

      <div
        ref="messagesContainer"
        class="flex-grow overflow-y-auto px-4 py-3 space-y-1"
        style="background-image: url(/assets/images/bg_chat.png); background-repeat: repeat; background-size: 170px;"
      >
        <div
          v-if="isLoadingMessages"
          class="flex items-center justify-center py-12"
        >
          <span class="i-lucide-loader-2 size-5 text-n-brand animate-spin" />
        </div>
        <template v-else>
          <div v-for="(item, idx) in messageGroups" :key="idx">
            <div
              v-if="item.type === 'date'"
              class="flex items-center justify-center py-2"
            >
              <span
                class="text-[10px] text-n-slate-9 bg-n-alpha-1 px-2 py-0.5 rounded-full"
              >
                {{ item.label }}
              </span>
            </div>
            <div v-else-if="isActivity(item.data)" class="flex items-center justify-center py-1.5">
              <span
                class="text-[10px] text-n-slate-9 bg-n-alpha-1 dark:bg-n-alpha-2 px-3 py-1 rounded-full text-center max-w-[85%] leading-snug"
              >
                {{ item.data.content }}
                <span class="opacity-60 ml-1">{{ formatTime(item.data.created_at) }}</span>
              </span>
            </div>
            <div
              v-else
              class="flex mb-1.5"
              :class="isOutgoing(item.data) ? 'justify-end' : 'justify-start'"
            >
              <div
                class="max-w-[80%] px-3 py-2 rounded-xl text-xs leading-relaxed"
                :class="
                  item.data.private
                    ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-900 dark:text-yellow-100 rounded-br-sm border border-yellow-200 dark:border-yellow-800/40'
                    : isOutgoing(item.data)
                      ? 'bg-n-brand text-white rounded-br-sm'
                      : 'bg-white dark:bg-n-slate-3 text-n-slate-12 rounded-bl-sm shadow-sm'
                "
              >
                <p
                  v-if="isOutgoing(item.data) && item.data.sender?.name"
                  class="text-[10px] font-semibold mb-0.5 opacity-80"
                >
                  {{ item.data.sender.name }}
                </p>
                <div
                  v-if="item.data.private"
                  class="flex items-center gap-1 mb-1 text-[10px] opacity-70"
                >
                  <span class="i-lucide-lock size-2.5" />
                  Nota privada
                </div>
                <p class="whitespace-pre-wrap break-words">
                  {{ item.data.content }}
                </p>
                <div
                  v-if="item.data.attachments && item.data.attachments.length"
                  class="mt-1"
                >
                  <div
                    v-for="att in item.data.attachments"
                    :key="att.id"
                    class="flex items-center gap-1 text-[10px] opacity-80"
                  >
                    <span class="i-lucide-paperclip size-2.5" />
                    {{ att.file_name || 'Anexo' }}
                  </div>
                </div>
                <p class="text-[10px] mt-1 opacity-60 text-right">
                  {{ formatTime(item.data.created_at) }}
                </p>
              </div>
            </div>
          </div>
        </template>
      </div>

      <div class="border-t border-n-weak bg-n-surface-1 px-4 py-3 space-y-2">
        <div>
          <label
            class="block text-[10px] font-semibold text-n-slate-9 uppercase tracking-wider mb-1"
          >
            Reatribuir conversa
          </label>
          <div class="flex items-center gap-2">
            <OnClickOutside
              class="relative flex-grow"
              @trigger="showAgentDropdown = false"
            >
              <button
                class="w-full flex items-center justify-between px-3 py-1.5 text-xs bg-white dark:bg-n-slate-2 border border-n-weak rounded-lg text-left transition-colors hover:border-n-slate-6"
                :class="
                  showAgentDropdown
                    ? 'ring-1 ring-n-brand border-n-brand'
                    : ''
                "
                @click="showAgentDropdown = !showAgentDropdown"
              >
                <span
                  :class="
                    selectedAgentName
                      ? 'text-n-slate-12'
                      : 'text-n-slate-8'
                  "
                >
                  {{ selectedAgentName || 'Selecionar agente...' }}
                </span>
                <span
                  class="i-lucide-chevron-up size-3.5 text-n-slate-8 transition-transform"
                  :class="showAgentDropdown ? '' : 'rotate-180'"
                />
              </button>
              <div
                v-if="showAgentDropdown"
                class="absolute bottom-full left-0 right-0 mb-1 bg-white dark:bg-n-slate-2 border border-n-weak rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto"
              >
                <button
                  v-for="agent in agents"
                  :key="agent.id"
                  class="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left hover:bg-n-alpha-1 transition-colors"
                  :class="
                    selectedAgentId === agent.id
                      ? 'text-n-brand font-semibold'
                      : 'text-n-slate-11'
                  "
                  @click="pickAgent(agent)"
                >
                  {{ agent.name }}
                  <span
                    v-if="selectedAgentId === agent.id"
                    class="i-lucide-check size-3.5 ml-auto text-n-brand"
                  />
                </button>
              </div>
            </OnClickOutside>
            <button
              :disabled="!selectedAgentId || isReassigning"
              class="px-3 py-1.5 text-xs font-medium bg-n-brand text-white rounded-lg hover:bg-n-brand/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex items-center gap-1.5"
              @click="reassignAgent"
            >
              <span
                v-if="isReassigning"
                class="i-lucide-loader-2 size-3 animate-spin"
              />
              <span class="i-lucide-user-round-check size-3.5" />
              Reatribuir
            </button>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <button
            class="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            @click="resolveConversation"
          >
            <span class="i-lucide-check-circle size-3.5" />
            Resolver
          </button>
          <button
            class="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-n-alpha-2 text-n-slate-12 rounded-lg hover:bg-n-alpha-3 transition-colors"
            @click="openFullConversation"
          >
            <span class="i-lucide-maximize-2 size-3.5" />
            Abrir conversa
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>
